import { createClient } from '@/utils/supabase/client';

// HACK: Define types locally to avoid import from a deleted file
type BlogPost = any;
type UserProfile = any;

export interface CreatePostPayload extends Omit<Partial<BlogPost>, 'tags' | 'seo_keywords'> {
  title: string;
  slug: string;
  content: string;
  author_id: string;
  tags?: string; // Accept tags as a comma-separated string
  seo_keywords?: string; // Accept keywords as a comma-separated string
}

export const createPost = async (payload: CreatePostPayload) => {
  const supabase = createClient();
  const tagsArray = payload.tags ? payload.tags.split(',').map(tag => tag.trim()) : [];
  const seoKeywordsArray = payload.seo_keywords ? payload.seo_keywords.split(',').map(kw => kw.trim()) : [];

  const { data, error } = await supabase
    .from('blog_posts')
    .insert({
      ...payload,
      tags: tagsArray,
      seo_keywords: seoKeywordsArray,
      id: undefined, // Ensure id is not in the insert payload
    } as any)
    .select()
    .single();

  return { data, error };
};

export const updatePost = async (postId: string, payload: Partial<CreatePostPayload>) => {
  const supabase = createClient();
  const updatePayload: { [key: string]: any } = { ...payload };
  if (typeof payload.tags === 'string') {
    updatePayload.tags = payload.tags.split(',').map(tag => tag.trim());
  }
  if (typeof payload.seo_keywords === 'string') {
    updatePayload.seo_keywords = payload.seo_keywords.split(',').map(kw => kw.trim());
  }
  
  const { data, error } = await supabase
    .from('blog_posts')
    .update(updatePayload)
    .eq('id', postId)
    .select()
    .maybeSingle();

  return { data, error };
};

export const blogService = {
  fetchPosts: async (page = 1, perPage = 10) => {
    const supabase = createClient();
    const { data, error, count } = await supabase
      .from('blog_posts')
      .select(`
        *,
        author:user_profiles(*)
      `, { count: 'exact' })
      .order('published_at', { ascending: false })
      .range((page - 1) * perPage, page * perPage - 1);

    return { data, error, count };
  },

  fetchBySlug: async (slug: string) => {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('blog_posts')
      .select(`
        *,
        author:user_profiles(*)
      `)
      .eq('slug', slug)
      .single();

    return { data, error };
  },
};

// Other utility functions, if any
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
};

export const calculateReadingTime = (content: string): number => {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
};
